import { isFrontendOnlyColumnId } from "@selector/shared/jsx";
import type { components } from "@selector/shared/openapi";
import type { ColDef, ColGroupDef, ColumnState } from "ag-grid-enterprise";

export type ColumnResponse = components["schemas"]["ColumnResponse"];
export type ColumnSparseResponse = components["schemas"]["ColumnSparseResponse"];
export type ColumnRequest = components["schemas"]["ColumnRequest"];
export type CustomSortModel = components["schemas"]["SortModel"][];

const isGroupDef = (def: ColDef | ColGroupDef): def is ColGroupDef =>
  Array.isArray((def as ColGroupDef).children) && (def as ColGroupDef).children.length > 0;

const collectLeafDefs = (columnDefs: (ColDef | ColGroupDef)[] | undefined): Map<string, ColDef> => {
  const leafDefs = new Map<string, ColDef>();

  if (!columnDefs) {
    return leafDefs;
  }

  const visit = (defs: (ColDef | ColGroupDef)[]) => {
    for (const def of defs) {
      if (isGroupDef(def)) {
        if (def.children) {
          visit(def.children);
        }
        continue;
      }

      const leaf = def as ColDef;

      if (!leaf.colId) {
        continue;
      }

      leafDefs.set(leaf.colId, leaf);
    }
  };

  visit(columnDefs);

  return leafDefs;
};

/**
 * Removes frontend-only columns (e.g. navigation helpers) from a sparse column tree,
 * pruning empty groups along the way.
 */
export const filterOutFrontendOnlyColumns = <T extends ColumnSparseResponse | ColumnResponse>(
  columns: T[] | null | undefined,
): T[] => {
  if (!columns) {
    return [];
  }

  const filterColumns = (nodes: T[]): T[] => {
    return nodes
      .map((node) => {
        if (!node || isFrontendOnlyColumnId(node.colId)) {
          return null;
        }

        if (node.children && node.children.length > 0) {
          const filteredChildren = filterColumns(node.children as T[]);

          if (filteredChildren.length === 0) {
            return null;
          }

          return { ...node, children: filteredChildren };
        }

        return node;
      })
      .filter((node): node is T => node != null);
  };

  return filterColumns(columns);
};

/**
 * Converts the current AG Grid column state into the sparse column representation expected by the API.
 * Filters out utility columns (e.g. selection checkboxes) by matching against the active column definitions.
 */
export const collectColumnsFromState = (
  columnState: ColumnState[] | undefined,
  columnDefs: (ColDef | ColGroupDef)[] | undefined,
): ColumnSparseResponse[] => {
  if (!columnState || columnState.length === 0) {
    return [];
  }

  const leafDefs = collectLeafDefs(columnDefs);

  const sparseColumns = columnState.flatMap((state) => {
    const colId = state.colId;

    if (!colId || isFrontendOnlyColumnId(colId)) {
      return [];
    }

    const def = leafDefs.get(colId);

    if (!def) {
      return [];
    }

    return [
      {
        colId,
        field: def.field ?? colId,
        fixed: Boolean(def.lockPinned || def.lockPosition === "left"),
        headerName: def.headerName ?? def.field ?? colId,
        hide: Boolean(state.hide),
      } satisfies ColumnSparseResponse,
    ];
  });

  return filterOutFrontendOnlyColumns(sparseColumns);
};

/**
 * Returns the current column set extended with any previously-known columns that
 * are no longer present in the grid. Removed columns are marked as hidden so
 * templates can record the difference.
 */
export const mergeColumnsWithRemoved = (
  currentColumns: ColumnSparseResponse[] | undefined,
  previousColumns: ColumnSparseResponse[] | undefined,
): ColumnSparseResponse[] => {
  const current = filterOutFrontendOnlyColumns(currentColumns);

  if (!previousColumns || previousColumns.length === 0) {
    return current;
  }

  const merged = [...current];
  const currentIds = new Set(current.map((column) => column.colId).filter((colId): colId is string => !!colId));

  for (const previous of filterOutFrontendOnlyColumns(previousColumns)) {
    if (!previous.colId || currentIds.has(previous.colId)) {
      continue;
    }

    merged.push({
      ...previous,
      hide: !previous.fixed,
    });
  }

  return merged;
};

export const toColumnRequests = (
  columns: (ColumnSparseResponse | ColumnRequest)[] | null | undefined,
): ColumnRequest[] => {
  if (!columns) return [];
  return columns
    .flatMap((column) => {
      if (!column?.colId || isFrontendOnlyColumnId(column.colId)) return [];

      const sparseColumn = column as ColumnSparseResponse;
      const hasChildren = Array.isArray(sparseColumn.children) && sparseColumn.children.length > 0;

      if (hasChildren) {
        return sparseColumn.children
          ?.filter((child) => !!child?.colId && !child.hide && !isFrontendOnlyColumnId(child.colId))
          .map((child) => ({ colId: child.colId! }) satisfies ColumnRequest);
      }

      if (sparseColumn.hide) {
        return [];
      }

      return [{ colId: sparseColumn.colId } satisfies ColumnRequest];
    })
    .filter((col): col is ColumnRequest => col != null);
};
